import type { CSSProperties, ReactNode } from "react";
import { pathFor, type RouteId } from "../../../app/router/routes";
import { KALQY_CONTACT, KALQY_SOCIAL, type MarketingCopy } from "../copy";
import { GAME_PAGE_IDS, marketingGame } from "../games";
import { goToSection, prefersReducedMotion } from "../useSectionSpy";
import {
  ArrowUpIcon,
  InstagramIcon,
  LinkedInIcon,
  LocationIcon,
  MailIcon,
  PhoneIcon,
  YouTubeIcon,
} from "../icons";

/**
 * The footer — a garden edge, a line of credits, and nothing else.
 *
 * 🔴 IT WAS A SECOND LANDING PAGE. Four columns: a brand block with a
 * positioning paragraph, an "Explore" nav duplicating the header's, an "At a
 * glance" list of four facts the trust strip already carries, and a boxed panel
 * with a heading, a body and the page's THIRD sign-in button. A footer's job is
 * to end a page, and a reader who has arrived here has either signed in from the
 * header or is looking for who made this. So: who made this, where, and what it
 * is built to.
 *
 * 🔴 AND IT HAS NO CONTROLS AT ALL NOW — no nav, no language switch. Both were
 * the header's, repeated: the header is sticky, so "The games", "For schools"
 * and the language toggle are on screen at the bottom of the page anyway, two
 * hundred pixels above where a duplicate would sit. A footer whose every item
 * is already visible is not a navigation aid, it is furniture. What is left is
 * the one thing the header cannot say: who publishes this.
 *
 * 🔴 THE GARDEN IS THE PRODUCT'S OWN VOCABULARY, NOT A NEW ONE. Leo hops across
 * lily pads, the letters live in a lagoon, and the child's world is drawn in
 * leaf and sky. Ending the adult page on a grass line with three small flowers
 * says the same thing the games say, in the one place a marketing page can
 * afford a picture that carries no argument. It is `aria-hidden` throughout —
 * decoration that announces itself to a screen reader is not decoration.
 *
 * ⚠️ IT WENT WHITE ONCE AND CAME BACK. The block was flipped to `surface`
 * (#FFFFFF) with `ink` type, and the green was restored deliberately after
 * seeing both. Worth recording because the white version was not broken — it
 * measured *better*, and it still lost:
 *
 *   - Contrast improved across the board. `ink` on white is 15.8:1 against
 *     `chalk` on green at 5.9:1, and the copyright's `muted` 5.1:1 needed no
 *     darker bar under it, so `FOREST_DEEP` could be deleted outright.
 *   - The garden lost its whole idea. The meadow is *cut out of* this block —
 *     the silhouette IS the page/footer boundary. White above and white below
 *     leaves no field to cut from, and the grass drops to a decorative band
 *     sitting on a boundary rather than being one.
 *   - The mark got worse. It is a bright multicoloured chip, and a deep flat
 *     green flatters it in a way that white does not.
 *
 * The lesson is that the ratios were never what made this footer work, so do not
 * re-flip it on the strength of them.
 *
 * ⚠️ NO GREY. `design-system.md` §2, in bold: *"No grey, anywhere. Every neutral
 * is warm."* The greens are `leaf`, the neutrals are `ink` / `ink-soft` / `muted`
 * / `line`, and the type on this block is `chalk` / `chalk-soft` — warm rather
 * than pure white, so they stay inside that rule. Nothing here is a literal hex.
 */

/**
 * ⚠️ NO `spy` ANY MORE. The footer's links are all routes now — the four game
 * pages and Connect — so it renders identically on the home page and on every
 * game page, where there are no home-page sections to scroll to. Sections are
 * the header's job, done once, at the top of a page that follows the reader down.
 */
export interface SiteFooterProps {
  t: MarketingCopy;
}

/**
 * The three greens the block is made of.
 *
 * 🔴 MIXED FROM TOKENS, NOT TYPED AS HEXES. `success` (#0F8A6A) is the deepest
 * green `design-tokens.css` has and it is a touch tealer than a meadow wants;
 * pulling it toward `ink` gives the forest this block needs while keeping both
 * ends in the token file, where a rebrand can move them. ENGINEERING-GUIDE.md §8: *"import
 * design-tokens.css. Do not retype the hexes."*
 *
 * ⚠️ THE TWO DEPTHS ARE A CONTRAST DECISION, NOT A TASTE ONE. `chalk` on
 * `FOREST` measures about 5.9:1 and clears §11's 4.5 floor; `chalk-soft` on the
 * same green is 4.0:1 and does not. The bottom bar is darker precisely so the
 * quieter of the two chalk tones can be used on it — 5.2:1 — instead of
 * shouting the copyright in the same weight as everything else.
 *
 * ⚠️ WHICH IS WHY `FOREST_DEEP` MUST NOT BE "TIDIED AWAY" AS A NEAR-DUPLICATE OF
 * `FOREST`. It is four percentage points from its neighbour and looks like
 * redundancy; it is the only reason one line of type on this page is legible.
 */
const FOREST = "color-mix(in srgb, var(--color-success) 58%, var(--color-ink))";
const FOREST_DEEP =
  "color-mix(in srgb, var(--color-success) 44%, var(--color-ink))";
/**
 * The far row of grass.
 *
 * 🔴 MIXED TOWARD `leaf`, NOT TOWARD `canvas`, AND THAT ONE WORD WAS THE WHOLE
 * DEFECT. Lightening a green with cream does not give a paler green — it gives
 * a sage GREY (#6D9686 at the ratio this used), and grey is the one thing
 * `design-system.md` §2 forbids in bold. On a deep green block it read as dirt
 * on the silhouette: a washed-out fringe along the top edge and a broken pale
 * line showing through every gap in the front row. Mixing toward the brand
 * green instead keeps the row unmistakably green while still standing behind
 * the other two.
 */
const FOREST_FAR = `color-mix(in srgb, ${FOREST} 72%, var(--color-leaf))`;

export function SiteFooter({ t }: SiteFooterProps) {
  /*
   * 🔴 THE FOOTER IS A SOLID BLOCK WHOSE TOP EDGE IS THE PICTURE.
   *
   * Every earlier version made the garden a *strip* — a band of illustration
   * with page above it and words below it — and every one of them read as three
   * stacked things. The edge is the whole idea: one block of forest green, cut
   * along the top by the meadow growing out of it, so the silhouette IS the
   * boundary between the page and the footer rather than a decoration sitting
   * on that boundary. Nothing separates the picture from the block, because
   * they are the same object.
   *
   * ⚠️ WHICH IS ALSO WHY THE TYPE IS CREAM. `chalk` and `chalk-soft` exist in
   * the token file for exactly this — *"text written ON a dark surface… warm
   * rather than pure white, so they stay inside the no-grey rule"* — and the
   * mark, which is a bright multicoloured chip, has never had a background that
   * suits it better than a deep flat green.
   *
   * ⚠️ THE PAGE ABOVE THIS IS ALREADY PURE WHITE, WHICH IS THE THING TO CHECK
   * BEFORE TOUCHING THIS COLOUR AGAIN. `html`, `body` and `main` all paint
   * nothing and the page's surface token is #FFFFFF, so "make the background
   * white" is already true of everything above the grass line. This block is
   * the page's one deliberate field of colour, not an oversight.
   */
  return (
    <footer className="relative" style={{ backgroundColor: FOREST }}>
      {/* The meadow, growing out of the top edge of the block. */}
      <GardenEdge />

      {/*
        🔴 FOUR COLUMNS, AND EVERY LINK IN THEM GOES SOMEWHERE THAT EXISTS.
        The reference footer this follows carries links, a newsletter and a
        contact block. This used to carry only the first, and the note here
        explained why: *"there is no newsletter endpoint in this product and no
        published support address in any document"*, `copy.ts` opens by saying
        *"nothing here is a claim the docs do not make"*, and an invented
        `support@` is exactly that claim.

        🔴 THE CONTACT HALF OF THAT OBJECTION IS SPENT, AND THE NEWSLETTER HALF
        IS NOT. `info@kalqy.in`, the Bengaluru address, the phone number and the
        three channels are all published by Kalqy on its own public site, so
        printing them reports a fact rather than inventing one — `KALQY_CONTACT`
        and `KALQY_SOCIAL` in `copy.ts` hold them. There is still no subscribe
        endpoint, so there is still no subscribe box; a form that posts nowhere
        would be the same lie it always was.

        ⚠️ THE FOUR DOORS ARE PLAIN `href`s, THE TWO SECTIONS ARE NOT. `#/play`
        is a route and the hash router is built to receive it; `#games` is an
        anchor on this page, and writing it to `location.hash` fires
        `hashchange`, fails `matchRoute`, and pushes a junk entry into the back
        stack — `useSectionSpy` records that failure in full. Sections scroll
        through the spy; routes are links, so they open in a new tab on a
        middle-click like any other link.
      */}
      {/*
        ⚠️ FOUR COLUMNS NOW, AND THE BREAKPOINT GREW A MIDDLE STEP. Three columns
        went `1fr` → `1.6fr_1fr_1fr` straight from phone to tablet; a fourth at
        that width would give each nav about 140 px and wrap "School admin" onto
        two lines. So `sm` takes three columns and `lg` takes four, and the
        contact column — the widest, because a street address is the longest
        string on the page — gets the largest fraction at both.

        🔴 AND THE BRAND BLOCK SPANS THE WHOLE ROW AT `sm`, WHICH IS NOT A TASTE
        CALL — IT IS THE FIX FOR A 500 px HOLE. A grid row is as tall as its
        tallest cell, and this one holds a 96 px wordmark, a tagline, a
        two-line paragraph and a row of channel buttons. Set beside a nav of
        three links it left that nav floating at the top of half a screen of
        white, with the remaining columns pushed into a second row. Full width
        at `sm`, one column of four at `lg` where there is room for it to sit
        alongside.
      */}
      {/* ⚠️ GENEROUS VERTICAL ROOM ON PURPOSE. The footer is the page's last
          screen; at `pt-8` it read as a band squeezed under the garden rather
          than as a place to land, so it now breathes like the sections above it. */}
      <div className="mx-auto grid w-full max-w-[1240px] gap-12 px-6 pt-14 pb-20 text-center sm:grid-cols-[1fr_1fr_1.3fr] sm:gap-10 sm:text-left lg:grid-cols-[1.5fr_1fr_1fr_1.35fr] lg:px-10 lg:pt-20 lg:pb-28">
        <div className="sm:col-span-3 lg:col-span-1">
          <a
            href="#/"
            aria-label={t.homeLabel}
            /* ⚠️ `ring-chalk`, AND IT TRACKS THE BLOCK COLOUR. Cream on forest is
             visible; cream on white is a keyboard user who cannot see where they
             are. If this block is ever flipped light again, this is the line that
             silently breaks — it is invisible in a screenshot and only shows up
             when someone tabs the page. */
            className="inline-block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-chalk"
          >
            {/*
            🔴 THE MARK IS THE FOOTER NOW, SO IT IS SIZED LIKE ONE. At h-11 it
            was a nav logo that had wandered to the bottom of the page; at 96 px
            it is the thing the page ends on and everything else is a caption
            under it. The file is 896 × 264, so even at 96 px it renders below
            its native size and stays sharp on a 2× screen.

            🔴 THE BOTTOM 3.4% IS CLIPPED, AND THAT IS THE "SOMETHING BEHIND THE
            LOGO". The artwork's white letter-shapes overhang the coloured chip
            by eight of its 264 rows — invisible for as long as this sat on
            cream, and a row of pale blobs the moment it sat on anything darker.
            A per-row scan of the PNG puts the chip's last solid row at 255, so
            the crop takes rows 256–263 and nothing else. Fixing the asset would
            be better; cropping the eight rows it should not have is what can be
            done from here.

            ⚠️ `width`/`height` ARE DECLARED. Without the intrinsic ratio the
            browser lays this block out at zero height and then reflows when the
            PNG arrives — a 96 px jump in the last thing on the page, which is
            exactly where a reader is when they notice it.

            ⚠️ AND IT SHRINKS ON A PHONE. 96 px of wordmark is 326 px wide, which
            is most of a 390 px screen.
          */}
            <img
              src="/images/kalqy-brand-logo.png"
              alt="Kalqy"
              width={896}
              height={264}
              loading="lazy"
              decoding="async"
              style={{ clipPath: "inset(0 0 3.4% 0)" }}
              className="h-16 w-auto object-contain sm:h-24"
            />
          </a>

          {/* ⚠️ WIDER TRACKING, NOT CAPITALS. `design-system.md` §4 forbids
            all-caps micro-labels outright; letter-spacing gives the three words
            the same considered, spaced-out feel without breaking that rule. */}
          <p className="mt-5 font-dash text-dash-sm font-bold tracking-[0.16em] text-chalk">
            {t.footer.tagline}
          </p>

          <p className="mx-auto mt-4 max-w-[38ch] font-dash text-dash-xs text-chalk-soft sm:mx-0">
            {t.footer.positioning}
          </p>

          {/*
          🔴 THE CHANNELS SIT UNDER THE MARK, NOT IN A COLUMN OF THEIR OWN. They
          are three 40 px targets and a heading — as a fourth nav column that is
          a mostly empty cell, and it would push the contact block off `lg` onto
          a fifth track. Under the positioning line they finish the brand block:
          who this is, what it does, where to find it.

          ⚠️ THE HEADING IS A REAL `<h2>`, THE SAME AS THE NAV COLUMNS'. Three
          icon links with no accessible group name are three orphans in the
          landmark tree.
        */}
          <h2 className="mt-7 font-dash text-dash-xs font-extrabold tracking-wide text-chalk">
            {t.footer.connect}
          </h2>
          <ul className="mt-3.5 flex justify-center gap-3 sm:justify-start">
            <li>
              <SocialLink
                href={KALQY_SOCIAL.linkedin}
                label={t.footer.social.linkedin}
              >
                <LinkedInIcon className="h-[18px] w-[18px]" />
              </SocialLink>
            </li>
            <li>
              <SocialLink
                href={KALQY_SOCIAL.instagram}
                label={t.footer.social.instagram}
              >
                <InstagramIcon className="h-[18px] w-[18px]" />
              </SocialLink>
            </li>
            <li>
              <SocialLink
                href={KALQY_SOCIAL.youtube}
                label={t.footer.social.youtube}
              >
                <YouTubeIcon className="h-[18px] w-[18px]" />
              </SocialLink>
            </li>
          </ul>
        </div>

        {/* ── Explore: the two sections of this page, and the price list ─── */}
        {/* ⚠️ THE RULES MOVED TO `lg`, WITH THE FOUR-COLUMN LAYOUT. At `sm` the
            grid is two columns, so a left rule on both navs would draw a line
            down the middle of the block and another down its right-hand edge. */}
        <nav
          aria-label={t.footer.platform}
          className="lg:border-l lg:border-chalk/15 lg:pl-8"
        >
          <FooterHeading>{t.footer.platform}</FooterHeading>
          <ul className="mt-4 space-y-3">
            <li>
              <FooterLink href={pathFor("for-schools")}>{t.nav.schools}</FooterLink>
            </li>
            {GAME_PAGE_IDS.map((id) => (
              <li key={id}>
                {/* The route id IS the game id for a game page — see `routes.ts`. */}
                <FooterLink href={pathFor(id as RouteId)}>{marketingGame(id).title}</FooterLink>
              </li>
            ))}
            <li>
              {/* Privacy is a panel on the home page: scrolled to in place there,
                  opened-then-scrolled from anywhere else (`goToSection`). */}
              <a
                href="#/"
                onClick={(e) => {
                  if (goToSection("privacy")) e.preventDefault();
                }}
                className={FOOTER_LINK}
              >
                {t.nav.privacy}
              </a>
            </li>
            <li>
              <FooterLink href={pathFor("contact-sales")}>{t.footer.connectPage}</FooterLink>
            </li>
          </ul>
        </nav>

        {/* ── The four doors ──────────────────────────────────────────────── */}
        <nav
          aria-label={t.footer.signIn}
          className="lg:border-l lg:border-chalk/15 lg:pl-8"
        >
          <FooterHeading>{t.footer.signIn}</FooterHeading>
          <ul className="mt-4 space-y-2.5">
            <li>
              <FooterLink href="#/play">{t.footer.doors.child}</FooterLink>
            </li>
            <li>
              <FooterLink href="#/parent/signin">
                {t.footer.doors.parent}
              </FooterLink>
            </li>
            <li>
              <FooterLink href="#/teacher/signin">
                {t.footer.doors.teacher}
              </FooterLink>
            </li>
            <li>
              <FooterLink href="#/school/signin">
                {t.footer.doors.school}
              </FooterLink>
            </li>
          </ul>
        </nav>

        {/*
          ── Get in touch ────────────────────────────────────────────────────
          🔴 TWO OF THE THREE ROWS ARE LINKS, AND THE THIRD IS NOT. `mailto:` and
          `tel:` are what a reader on a phone actually wants — a school
          administrator reading this on a handset taps the number and it dials.
          The address is not a link because there is nowhere honest to send it:
          a maps URL would be a third-party destination this product does not
          publish, guessed from a street string.

          ⚠️ IT IS AN `<address>`, WHICH IS THE ONE ELEMENT HTML HAS FOR EXACTLY
          THIS AND IS ALMOST ALWAYS WRONG WHEN USED. It means "contact details
          for the nearest article or the document" — not "a postal address" —
          and inside a page footer, for the publisher's own details, is the
          textbook case rather than the abuse of it.

          ⚠️ `not-italic` BECAUSE BROWSERS ITALICISE `<address>` BY DEFAULT, and
          three italic lines in a block where nothing else is italic reads as an
          accident rather than as emphasis.
        */}
        <address className="not-italic lg:border-l lg:border-chalk/15 lg:pl-8">
          <FooterHeading>{t.footer.contact}</FooterHeading>
          <ul className="mt-4 space-y-3">
            <ContactRow
              icon={<MailIcon className="h-4 w-4" />}
              label={t.footer.contactLabels.email}
            >
              <a href={`mailto:${KALQY_CONTACT.email}`} className={FOOTER_LINK}>
                {KALQY_CONTACT.email}
              </a>
            </ContactRow>
            <ContactRow
              icon={<PhoneIcon className="h-4 w-4" />}
              label={t.footer.contactLabels.phone}
            >
              {/* ⚠️ `tabular` SO THE DIGITS ARE MONOSPACED, the same class the
                  copyright's year uses. A phone number set in proportional
                  figures is the one string on this page a reader copies by eye. */}
              <a
                href={KALQY_CONTACT.phoneHref}
                className={`tabular ${FOOTER_LINK}`}
              >
                {KALQY_CONTACT.phone}
              </a>
            </ContactRow>
            <ContactRow
              icon={<LocationIcon className="h-4 w-4" />}
              label={t.footer.contactLabels.address}
            >
              <span className="font-dash text-dash-sm text-chalk-soft">
                {KALQY_CONTACT.address}
              </span>
            </ContactRow>
          </ul>
        </address>
      </div>

      {/*
        ⚠️ THE COPYRIGHT GETS ITS OWN DARKER BAR RATHER THAN A HAIRLINE. A rule
        across a coloured block is a line drawn on a wall; a shade change is the
        wall being built in two courses. It is also what earns `chalk-soft`:
        4.0:1 on the block above, 5.2:1 down here.
      */}
      <div className="relative" style={{ backgroundColor: FOREST_DEEP }}>
        {/*
          🔴 THE BACK-TO-TOP BUTTON CAME BACK WITH THE PAGE THAT NEEDS ONE.
          `useSectionSpy` removed its `toTop` callback with the note that *"a
          back-to-top button belongs on a page long enough to get lost in; this
          one is a hero, a row of facts and two sections"*. The page is now a
          hero, a trust strip, five game cards, a schools panel and a closing
          banner — roughly six screens on a laptop — and the comp draws the
          control, so the reasoning that removed it no longer holds.

          ⚠️ IT SCROLLS THE WINDOW DIRECTLY RATHER THAN GOING THROUGH THE SPY.
          `goTo` is typed to `SectionKey`, and the top of the page is not a
          section — widening that union to carry a sentinel `'top'` would put a
          non-section into the type that keeps the nav honest. It also honours
          the motion preference the same way `goTo` does, for the same reason.

          ⚠️ `focus({ preventScroll: true })` IS NOT NEEDED HERE and is not used:
          nothing above receives focus, and the header is the first tab stop
          after the document start regardless.
        */}
        <button
          type="button"
          aria-label={t.footer.backToTop}
          onClick={() => {
            window.scrollTo({
              top: 0,
              behavior: prefersReducedMotion() ? "auto" : "smooth",
            });
          }}
          /* ⚠️ NO `ring-offset`. Tailwind's offset paints a ring-width band in
             `--tw-ring-offset-color`, which defaults to white — a white halo
             around a green button on a green bar. The ring sits flush instead,
             and `chalk` on `FOREST_DEEP` is the same pairing the copyright line
             already clears §11 with. */
          className="absolute -top-6 right-6 flex h-12 w-12 items-center justify-center rounded-pill text-chalk shadow-raised outline-none transition-transform duration-200 ease-dash hover:-translate-y-1 focus-visible:ring-2 focus-visible:ring-chalk lg:right-10"
          style={{ backgroundColor: FOREST }}
        >
          <ArrowUpIcon className="h-5 w-5" />
        </button>

        <div className="mx-auto w-full max-w-[1240px] px-6 py-6 text-center lg:px-10 lg:py-7">
          {/*
            ⚠️ THE YEAR IS COMPUTED. A hardcoded one is wrong every January, and
            a stale copyright line is the detail a procurement officer notices
            before any of the copy above it.

            ⚠️ `text-balance` FOR THE ONE ORPHAN THIS LINE PRODUCES on a 390 px
            screen, where it wrapped with "reserved." alone on the second line —
            on a centred block, the most visible kind of bad break.
          */}
          <p className="tabular mx-auto max-w-[46ch] text-balance font-dash text-dash-xs text-chalk-soft sm:max-w-none">
            {`© ${String(new Date().getFullYear())} ${t.footer.company} · ${t.footer.country} · ${t.footer.rights}`}
          </p>
        </div>
      </div>
    </footer>
  );
}

/* ────────────────────────────────────────────────────── THE GARDEN ── */

/**
 * A blade of grass: where it stands, how tall, how far it leans, how thick.
 *
 * 🔴 THE NUMBERS ARE IRREGULAR ON PURPOSE, AND THAT IS THE WHOLE DIFFERENCE.
 * The first version of this footer drew a row of identical semicircles, which
 * reads as a scalloped border — the eye finds the repeat instantly and stops
 * seeing grass. Real grass has no two blades the same height and none of them
 * stands straight, so every triple below is hand-varied: heights, spacing,
 * lean and thickness all differ, and the leans alternate direction.
 */
type Blade = readonly [x: number, height: number, lean: number, width: number];

/**
 * Blades → one tiling SVG path.
 *
 * Each blade is two quadratic curves — up one side to the tip and back down the
 * other — so it tapers to a point instead of ending in a flat cap. A 3 px base
 * strip binds them into turf; without it the mask would show canvas between
 * every blade at the ground line.
 *
 * ⚠️ COORDINATES ARE ROUNDED TO ONE DECIMAL. This string goes into a data URI
 * in the markup, and `20.000000000000004` × forty blades × three layers is real
 * bytes on a school connection for a difference nobody can see.
 */
function turfPath(
  width: number,
  height: number,
  blades: readonly Blade[],
): string {
  const n = (v: number): string => v.toFixed(1);
  const parts = blades.map(([x, h, lean, w]) => {
    const half = w / 2;
    const midY = height - h * 0.5;
    return (
      `M${n(x - half)} ${n(height)}` +
      `Q${n(x - half / 3 + lean * 0.35)} ${n(midY)} ${n(x + lean)} ${n(height - h)}` +
      `Q${n(x + half / 3 + lean * 0.35)} ${n(midY)} ${n(x + half)} ${n(height)}Z`
    );
  });
  /* ⚠️ A 2 px BASE, NOT 3. Three layers each contribute a base strip and they
     stack into one line along the bottom of the page — at 3 px that line was
     heavy enough to read as a painted rule under the grass. */
  return [`M0 ${n(height)}H${n(width)}V${n(height - 2)}H0Z`, ...parts].join("");
}

/**
 * A turf path → a repeating CSS mask.
 *
 * 🔴 A MASK, NOT A BACKGROUND IMAGE, SO THE COLOUR STAYS A TOKEN. A picture
 * would have to carry `#45B308` inside the data URI, and ENGINEERING-GUIDE.md §8 is
 * explicit — *"import design-tokens.css. Do not retype the hexes."* Masking a
 * plain `bg-leaf` element keeps every green in the token file, where a redesign
 * changes it once. The black below is not a colour: a CSS mask reads the alpha
 * channel, so it is a stencil.
 *
 * 🔴 AND IT TILES AT A FIXED SIZE RATHER THAN STRETCHING. One wide SVG with
 * `preserveAspectRatio="none"` is the obvious way to draw this and it is wrong
 * in both directions: on a 1920 px monitor every blade comes out 1.6× too fat,
 * and on a 375 px phone they are squeezed into spikes.
 */
function turfMask(
  width: number,
  height: number,
  blades: readonly Blade[],
  offsetX: number,
) {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${String(width)}" height="${String(height)}" ` +
    `viewBox="0 0 ${String(width)} ${String(height)}">` +
    `<path fill="#000" d="${turfPath(width, height, blades)}"/></svg>`;
  const url = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  const size = `${String(width)}px ${String(height)}px`;
  const position = `${String(offsetX)}px bottom`;

  return {
    maskImage: url,
    WebkitMaskImage: url,
    maskRepeat: "repeat-x",
    WebkitMaskRepeat: "repeat-x",
    maskSize: size,
    WebkitMaskSize: size,
    maskPosition: position,
    WebkitMaskPosition: position,
  } satisfies CSSProperties;
}

/*
 * Three rows, and the tile widths are deliberately not multiples of each other.
 *
 * 🔴 260 / 190 / 150 BEAT AGAINST EACH OTHER RATHER THAN LINING UP. Three
 * layers on the same rhythm repeat as one visible motif every tile — the
 * wallpaper effect — and the fix is arithmetic rather than more blades: these
 * three only realign every 7 410 px, which is wider than any screen this ships
 * to. It is the same trick a tiled texture uses to hide its seam.
 */
/**
 * One tuft: five blades fanning out of a single root.
 *
 * 🔴 GRASS GROWS IN CLUMPS, AND EVENLY SPACED SINGLE BLADES ARE WHY THE FIRST
 * PASS STILL READ AS A COMB. Twelve blades at twenty-pixel intervals is a
 * texture with a period; four tufts with gaps between them is a lawn. The fan
 * is what does it — the outer blades are shorter and lean hardest, exactly as
 * they do when a clump splays under its own weight.
 *
 * ⚠️ THE GAPS ARE AS IMPORTANT AS THE TUFTS. They are where the layer behind
 * shows through, which is the only reason three rows read as depth rather than
 * as one thick band.
 */
function tuft(cx: number, tall: number, thick: number): Blade[] {
  return [
    [cx - 9, tall * 0.55, -12, thick * 0.75],
    [cx - 4, tall * 0.84, -5, thick * 0.9],
    [cx + 1, tall, 2, thick],
    [cx + 6, tall * 0.78, 9, thick * 0.85],
    [cx + 11, tall * 0.48, 14, thick * 0.7],
  ];
}

/* Distant row: tall tufts, generous gaps, one lone blade in each gap so the
   spacing is not itself a rhythm. */
const BACK: readonly Blade[] = [
  ...tuft(30, 44, 13),
  [62, 21, 6, 9],
  ...tuft(96, 37, 12),
  [128, 26, -5, 10],
  ...tuft(160, 46, 14),
  [193, 19, 4, 9],
  ...tuft(224, 39, 12),
];

const MID: readonly Blade[] = [
  ...tuft(24, 32, 12),
  [54, 16, -5, 9],
  ...tuft(80, 27, 11),
  [108, 20, 5, 9],
  ...tuft(136, 33, 12),
  [166, 15, -4, 8],
];

/* Nearest row: short, and sparse enough that the green behind it shows in every
   gap. A dense front row is the solid dark stripe this replaced. */
const FRONT: readonly Blade[] = [
  ...tuft(20, 20, 10),
  [48, 11, 4, 8],
  ...tuft(72, 17, 9),
  [100, 13, -4, 8],
  ...tuft(124, 21, 10),
];

/**
 * One element's share of the breeze.
 *
 * 🔴 NO TWO CALLS SHARE A DURATION, AND THAT IS THE WHOLE ILLUSION. Wind is not
 * a metronome: nine things swaying on the same 8-second clock read as one object
 * wobbling, however gentle each one is. The durations below share no common
 * multiple, so the pattern never repeats inside a visit.
 *
 * ⚠️ THE DELAYS ARE NEGATIVE ON PURPOSE. A positive delay holds everything
 * still and then starts it together — the one moment that gives the trick away.
 * A negative delay starts each element part-way through its own cycle, so the
 * first painted frame already shows a bed mid-gust.
 */
function breeze(
  sway: string,
  seconds: number,
  startedSecondsAgo: number,
  tilt?: string,
) {
  return {
    "--kq-sway": sway,
    "--kq-dur": `${String(seconds)}s`,
    "--kq-delay": `-${String(startedSecondsAgo)}s`,
    ...(tilt === undefined ? {} : { "--kq-tilt": tilt }),
  } as CSSProperties;
}

/**
 * The garden along the footer's top edge.
 *
 * 🔴 THREE DEPTH LAYERS, AND THE FLOWERS ARE PLANTED *BETWEEN* THEM. The layer
 * order is far turf → far flowers → near turf → flowers → front turf, so two
 * different rows of grass cross the stems at two different heights. That
 * interleaving is what makes the bed read as having depth rather than as three
 * stickers stacked on a page.
 *
 * 🔴 THE FAR ROW IS BLURRED, AND THE BLUR IS ON A WRAPPER RATHER THAN THE
 * MASKED ELEMENT. Distance is depth of field before it is anything else — a
 * pale but perfectly sharp back row reads as a paler copy of the front one. The
 * wrapper matters: a `filter` on the masked element itself is applied *before*
 * the mask in the rendering model, so the blade edges would come back crisp.
 * Blurring the parent blurs the already-masked result, which is what softens
 * the silhouette.
 *
 * 🔴 THE PALE ROW IS FURTHEST AWAY. Distance on a light background washes
 * colour out rather than darkening it, so the far row is the palest, the middle
 * is the brand `leaf`, and the front carries `success` at its root — the deep
 * green from the token file — putting the shadow at the reader's feet where the
 * light does not reach. Three tints of one hue, every one a token.
 *
 * 🔴 AND IT MOVES NOW. Nine elements sway on their own clocks: the turf skews
 * from its roots, the flowers pivot and bend at theirs. `marketing.css` holds
 * the physics and the reasoning; what lives here is the casting — who sways how
 * far, how slowly, and how far out of step with its neighbours.
 *
 * ⚠️ THE WHOLE BREEZE STOPS UNDER `prefers-reduced-motion` AND UNDER THE IN-APP
 * REDUCE ANIMATION SETTING, and what is left is a drawn garden rather than an
 * empty strip — the flowers keep their resting lean from a static `transform`.
 * Ambient motion carrying no information is exactly what that preference is
 * for, and this is the only place on the page that loops forever.
 *
 * ⚠️ IT DEGRADES TO A GREEN BAND. If a browser ignores CSS masks these are
 * plain filled `div`s — a flat green edge, which is a plainer version of the
 * same thing rather than a broken one.
 */
function GardenEdge() {
  /*
   * 🔴 THE STRIP IS THE CUT, NOT A PICTURE ON TOP OF ONE. Its background is the
   * page's cream and everything drawn in it is the block's own green, so what
   * a reader sees is a single field of forest whose top edge happens to be
   * grass, bushes and flowers. The strip is 128px because the bushes need to
   * clear the meadow — a treeline that is the same height as the grass is just
   * taller grass.
   */
  return (
    /* ⚠️ `bg-surface`, NOT `bg-canvas`. This strip is the sky the meadow is cut
       out of — it is page background, not a component fill, so it has to be
       whatever the page is. On a white page `canvas` left a cream band sitting
       between the last section and the grass line, which reads as a stray
       divider rather than as sky. */
    <div
      aria-hidden="true"
      className="pointer-events-none relative h-32 w-full overflow-hidden bg-surface"
    >
      {/*
        🔴 THE ROWS ARE FLAT, NOT GRADIENTS, AND THAT IS THE REFERENCE'S WHOLE
        TRICK. Three tones of green made a pretty band of grass sitting on a
        page; one tone — the block's own — makes the same shapes read as the
        block itself, cut along the top. A silhouette cannot be shaded, or it
        stops being a silhouette and goes back to being an illustration.

        ⚠️ THE ROWS OVERHANG THE EDGES (`-inset-x-2`). Skewing a full-width
        element pulls its top corners inward, which would open a wedge of cream
        at each end of the strip. Eight pixels of overhang is more than the two
        the widest sway can take, and the container clips the rest.
      */}

      {/*
        The treeline: bushes, the tallest things on the edge, furthest back.

        🔴 THE FIRST AND LAST ONE RUN OFF THE FRAME (`-left-[4%]`, `left-[92%]`
        on a 170px bush). A row of complete bushes with clear cream either side
        of it reads as an ornament that was placed on the page; a treeline
        continuing past both edges reads as a place the page happens to end in
        the middle of. It is the cheapest trick in scene-setting and the corners
        were exactly where its absence showed.
      */}
      <Bush className="-left-[4%] h-[66px] w-[150px]" />
      <Bush className="left-[14%] h-[74px] w-[150px]" />
      <Bush className="left-[36%] hidden h-[58px] w-[120px] sm:block" />
      <Bush className="left-[62%] h-[80px] w-[170px]" />
      <Bush className="left-[92%] h-[70px] w-[170px]" />

      {/*
        Far row: tallest, palest, slowest to sway.

        🔴 THE BLUR CAME OFF. It was depth-of-field when this was an
        illustration on cream, and against a flat silhouette it reads as grey
        mist along the top edge — the one thing that made the block look dirty
        rather than cut. A single lighter tint of the same green does the depth
        without softening the edge.
      */}
      <div
        className="kq-breeze-turf absolute -inset-x-2 bottom-0 h-[46px]"
        style={{
          backgroundColor: FOREST_FAR,
          ...turfMask(260, 46, BACK, -70),
          ...breeze("2.4deg", 11, 3),
        }}
      />

      {/* Two flowers standing in the far row: small, soft and washed out, so
          they read as the same bed continuing back rather than as a second row
          of the same size. */}
      <FarFlower
        className="left-[27%] h-[38px] w-6 sm:h-[52px] sm:w-8"
        petal="fill-primary"
      />
      <FarFlower
        className="left-[69%] h-[34px] w-6 sm:h-[46px] sm:w-7"
        petal="fill-sunshine"
      />

      {/* The block's own green, and the row that carries the eye along the edge. */}
      <div
        className="kq-breeze-turf absolute -inset-x-2 bottom-0 h-[34px]"
        style={{
          backgroundColor: FOREST,
          ...turfMask(190, 34, MID, -25),
          ...breeze("1.7deg", 9.2, 1.4),
        }}
      />

      {/*
        The planting. Six stems at uneven intervals and four different heights:
        a row of identical flowers at identical spacing is a border pattern, and
        this page already learned that lesson once with the hedge.

        ⚠️ TWO OF THEM ARE `sm:` ONLY. Six flowers across 390 px is a crowd —
        they would overlap into a hedge of petals on the phone a parent opens a
        school's link on.

        ⚠️ AND THEY SHRINK ON A PHONE. The grass is a fixed pixel size at every
        width, so a flower sized for 1440 px is proportionally twice as large on
        a 390 px screen — it stops being a flower in a lawn and becomes a
        cartoon standing in front of one.
      */}
      <Flower
        className="left-[6%] h-[58px] w-8 sm:h-[82px] sm:w-11"
        petals={6}
        petal="fill-primary"
        eye="fill-sunshine"
        style={breeze("3.1deg", 7.7, 1.2, "-3deg")}
      />
      <Bud
        className="left-[19%] hidden h-[58px] w-8 sm:block"
        petal="fill-coral"
        style={breeze("2.6deg", 8.6, 3.4, "2deg")}
      />
      <Flower
        className="left-[37%] h-[48px] w-7 sm:h-[66px] sm:w-10"
        petals={5}
        petal="fill-sunshine"
        eye="fill-warning"
        style={breeze("2.8deg", 6.4, 0.6, "4deg")}
      />
      <Flower
        className="left-[59%] h-[54px] w-8 sm:h-[76px] sm:w-11"
        petals={6}
        petal="fill-sky"
        eye="fill-sunshine"
        style={breeze("3.4deg", 9.1, 2.8, "-2deg")}
      />
      <Bud
        className="left-[77%] hidden h-[52px] w-8 sm:block"
        petal="fill-grape"
        style={breeze("2.4deg", 7.1, 4.1, "-3deg")}
      />
      <Flower
        className="left-[84%] h-[46px] w-7 sm:h-[62px] sm:w-10"
        petals={5}
        petal="fill-coral"
        eye="fill-sunshine"
        style={breeze("3deg", 8.2, 1.9, "3deg")}
      />

      {/*
        Nearest row: short, deep, and drawn OVER the stems — this is the line
        that roots every flower in the ground, and the fastest of the three
        because the nearest thing in a moving scene is the one that appears to
        move most.

        ⚠️ IT IS THE SAME GREEN AS THE OTHER TWO, WHICH IS WHY THERE IS NO SEAM.
        Three rows in three tones would put two visible lines across the block;
        one tone leaves only the silhouette, and the depth comes from the far
        row's haze instead.
      */}
      <div
        className="kq-breeze-turf absolute -inset-x-2 bottom-0 h-[22px]"
        style={{
          backgroundColor: FOREST,
          ...turfMask(150, 22, FRONT, 0),
          ...breeze("1.1deg", 7.6, 2.2),
        }}
      />

      {/*
        Three seeds carried on the same air.

        ⚠️ SOFT, PALE, LOW AND ONLY THREE. This is the detail that tips a scene
        from "the plants are animated" into "there is wind" — the air itself is
        carrying something. Any more than three and it stops being a breeze and
        starts being weather. They reuse `kq-drift`, the hero's own confetti
        loop, rather than introducing a fourth kind of motion to this page.

        🔴 THE BLUR IS NOT DECORATION, IT IS THE DIFFERENCE BETWEEN A SEED AND A
        DEAD PIXEL. Sharp 4 px dots high in an empty cream band read as dust on
        the reader's screen — the first thing anybody does is wipe the monitor.
        Blurred, half-transparent, and kept down among the flower heads where
        the eye expects something to be, they read as pollen catching light.
      */}
      <span
        className="kq-drift absolute left-[22%] bottom-[38px] h-1.5 w-1.5 rounded-pill bg-sunshine/45 blur-[1px]"
        style={{ "--kq-delay": "0s" } as CSSProperties}
      />
      <span
        className="kq-drift absolute left-[48%] bottom-[30px] hidden h-1.5 w-1.5 rounded-pill bg-leaf/45 blur-[1px] sm:block"
        style={{ "--kq-delay": "-3.5s" } as CSSProperties}
      />
      <span
        className="kq-drift absolute left-[73%] bottom-[44px] h-1.5 w-1.5 rounded-pill bg-sunshine/40 blur-[1px]"
        style={{ "--kq-delay": "-6.2s" } as CSSProperties}
      />
    </div>
  );
}

/**
 * A column heading.
 *
 * ⚠️ `chalk`, NOT `sunshine` OR `leaf`. Both were tried and both fail §11 on
 * this green — sunshine measures 3.7:1 and leaf 2.4:1, where the floor is 4.5.
 * The heading is separated from the links under it by weight and spacing
 * rather than by colour, which is what the adult tier does everywhere else.
 */
function FooterHeading({ children }: { children: string }) {
  return (
    <h2 className="font-dash text-dash-xs font-extrabold tracking-wide text-chalk">
      {children}
    </h2>
  );
}

const FOOTER_LINK =
  "kq-underline font-dash text-dash-sm text-chalk-soft transition-colors duration-150 ease-dash hover:text-chalk";

/** A link to a route. A real `<a>`, so middle-click and copy-link both work. */
function FooterLink({ href, children }: { href: string; children: string }) {
  return (
    <a href={href} className={FOOTER_LINK}>
      {children}
    </a>
  );
}

/**
 * One channel: a 40 px target with a brand mark in it.
 *
 * 🔴 `rel="noreferrer"` AS WELL AS `noopener`. Every one of these opens a new
 * tab, and `target="_blank"` without `noopener` hands the opened page a live
 * `window.opener` handle to this one — a tabnabbing vector that has no upside
 * here. `noreferrer` implies `noopener` in modern browsers, but the pair is what
 * survives a reader on an old Safari.
 *
 * ⚠️ 40 px, NOT 32. `design-system.md` §9's touch floor is 40, and this is a row
 * of small icons on a page a parent opens on a phone — the most likely place in
 * the whole footer to miss a target. The icon inside stays 18 px; the padding
 * does the work.
 *
 * ⚠️ THE HOVER GOES TO `primary`, NOT TO CREAM. Magenta is this page's action
 * colour — every button on it, including the header's "Get started" — and it is
 * the one hover that works on a green block without the icon having to change
 * colour with it: `chalk` stays `chalk` across both states, so only the pill
 * moves. Filling with `chalk` instead would have forced the glyph to flip to a
 * dark green mid-hover, which is two changes where one will do.
 */
function SocialLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="flex h-10 w-10 items-center justify-center rounded-pill bg-chalk/15 text-chalk outline-none transition-colors duration-150 ease-dash hover:bg-primary focus-visible:ring-2 focus-visible:ring-chalk"
    >
      {children}
    </a>
  );
}

/**
 * One line of the contact block: a glyph, a hidden label, and the detail.
 *
 * ⚠️ `items-start` AND A `mt-0.5` ON THE ICON, NOT `items-center`. The address
 * wraps to three lines on a narrow column, and centring puts its pin halfway
 * down the paragraph. Aligning to the top and nudging the glyph onto the first
 * line's baseline is what keeps all three rows reading as a list.
 *
 * ⚠️ `shrink-0` ON THE ICON. In a flex row a 16 px SVG next to a long string is
 * the item that gets compressed — the pin would squash to an ellipse at exactly
 * the width where the address needs it most.
 */
function ContactRow({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) {
  return (
    <li className="flex justify-center gap-2.5 sm:justify-start">
      {/* ⚠️ `chalk-soft`, NOT `leaf` OR `success`. A green glyph on a green block
          is the obvious tie to the garden and it does not survive measurement:
          `leaf` lands at 2.9:1 and `success` below 2:1 here, against §11's 3:1
          floor for a non-text graphic. Cream at 4.0:1 clears it and reads as
          part of the type it labels rather than as a third green. */}
      <span aria-hidden="true" className="mt-0.5 shrink-0 text-chalk-soft">
        {icon}
      </span>
      <span className="max-w-[34ch] text-left">
        <span className="sr-only">{`${label}: `}</span>
        {children}
      </span>
    </li>
  );
}

/**
 * A bush on the treeline: overlapping crowns rising out of the block.
 *
 * 🔴 THIS IS THE SHAPE THE REFERENCE FOOTER IS BUILT FROM. Its top edge is a
 * band of trees and shrubs cut out of one solid green, and what makes it read
 * as a place rather than as a decorative border is that the silhouette has
 * *heights* — some things clear the line by twice what the rest do. Grass alone
 * gives a fringe; grass with bushes gives a horizon.
 *
 * ⚠️ SAME FILL AS THE BLOCK, ALWAYS. The moment a bush is a different green it
 * stops being part of the footer and becomes a sticker on it.
 *
 * ⚠️ CIRCLES, NOT A HAND-DRAWN PATH. Five overlapping crowns in one `<g>` are
 * impossible to get wrong at any size, where a bezier blob is a shape I cannot
 * check by reading it. `viewBox` is 200 × 100 with everything anchored to the
 * bottom edge, so the container's width and height do the composing.
 */
function Bush({ className }: { className: string }) {
  return (
    <svg
      viewBox="0 0 200 100"
      preserveAspectRatio="none"
      className={`absolute bottom-0 ${className}`}
      style={{ fill: FOREST }}
      role="presentation"
    >
      <circle cx="42" cy="66" r="34" />
      <circle cx="88" cy="46" r="42" />
      <circle cx="140" cy="60" r="36" />
      <circle cx="172" cy="76" r="26" />
      {/* ⚠️ THE BASE STRIP STOPS AT 86, NOT 72. It exists only to close the gaps
          between the crowns at the very bottom, and at 72 its flat top showed
          through the sparse patches of grass as a rectangular step in the
          skyline — the one straight line in a silhouette made of curves. At 86
          it is eleven pixels tall and sits entirely behind the front row. */}
      <rect x="0" y="86" width="200" height="14" />
    </svg>
  );
}

/**
 * One flower: a bending stem, two leaves, two rings of petals and a middle.
 *
 * 🔴 TWO RINGS, THE BACK ONE OFFSET BY HALF A PETAL. One ring of five ellipses
 * leaves gaps of canvas between the petals and reads as a cut-out star; a
 * second ring behind it, rotated half a step and slightly larger, fills those
 * gaps and gives the bloom a silhouette with depth. At 70% opacity the back
 * ring lightens where it shows past the front one, which is what a petal
 * catching light from behind actually looks like.
 *
 * 🔴 THE PETALS ARE ELLIPSES, NOT CIRCLES. Four circles in a cross is the shape
 * a child draws and it is what the first version rendered. Five or six tapered
 * petals radiating from a middle is what a flower looks like at this size, and
 * the count is a prop so no two flowers in the row are the same species.
 *
 * ⚠️ THE TWO LEAVES ARE DIFFERENT GREENS. One `leaf`, one `success` — the same
 * plant lit from one side. Two identical leaves is the tell of a stamp.
 *
 * ⚠️ THE STEM IS A CURVE. A vertical straight line reads as a lollipop stick;
 * one quadratic with its control point off-axis is the difference between
 * "planted" and "placed".
 */
function Flower({
  className,
  petals,
  petal,
  eye,
  style,
}: {
  className: string;
  /** 5 or 6. Different counts read as different flowers. */
  petals: number;
  petal: string;
  /** The middle. `sunshine` on a coloured flower, `warning` on the yellow one —
      §2's rule that sunshine is a fill and never sits on itself. */
  eye: string;
  /** The breeze: sway, duration, phase and resting tilt. */
  style: CSSProperties;
}) {
  const step = 360 / petals;

  return (
    <svg
      viewBox="0 0 40 100"
      className={`kq-breeze-stem absolute bottom-0 ${className}`}
      style={style}
      role="presentation"
    >
      <path
        className="stroke-success"
        strokeWidth="2.6"
        strokeLinecap="round"
        fill="none"
        d="M19 100Q15 68 20 42"
      />
      <path className="fill-leaf" d="M18 78q-13-1-16-11 11-3 16 11Z" />
      <path className="fill-success" d="M19 62q12-2 14-11-10-3-14 11Z" />

      {Array.from({ length: petals }, (_, i) => (
        <ellipse
          key={`back-${String(i)}`}
          className={`${petal} opacity-70`}
          cx="20"
          cy="17"
          rx="6.6"
          ry="12.6"
          transform={`rotate(${String(i * step + step / 2)} 20 30)`}
        />
      ))}
      {Array.from({ length: petals }, (_, i) => (
        <ellipse
          key={`front-${String(i)}`}
          className={petal}
          cx="20"
          cy="19"
          rx="6"
          ry="11.5"
          transform={`rotate(${String(i * step)} 20 30)`}
        />
      ))}

      {/* The middle, and a ring of stamen dots around it. Three marks instead
          of one is the difference between a flower and a bullseye. */}
      <circle className={eye} cx="20" cy="30" r="5.5" />
      <circle className="fill-ink/25" cx="17.6" cy="28.4" r="1" />
      <circle className="fill-ink/25" cx="22.2" cy="29.4" r="1" />
      <circle className="fill-ink/25" cx="19.4" cy="32.2" r="1" />
    </svg>
  );
}

/**
 * A bud that has not opened: a stem, a leaf, a closed teardrop and its calyx.
 *
 * ⚠️ IT EXISTS SO THE ROW IS NOT ALL ONE THING. A garden where every flower is
 * open at the same moment is a pattern; one where two are still closed is a
 * garden. It is also four paths, which is what lets it sit between the open
 * flowers without crowding them.
 */
function Bud({
  className,
  petal,
  style,
}: {
  className: string;
  petal: string;
  style: CSSProperties;
}) {
  return (
    <svg
      viewBox="0 0 24 100"
      className={`kq-breeze-stem absolute bottom-0 ${className}`}
      style={style}
      role="presentation"
    >
      <path
        className="stroke-success"
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
        d="M13 100Q9 74 12 52"
      />
      <path className="fill-leaf" d="M11 82q-9-1-11-8 8-2 11 8Z" />
      <path
        className={petal}
        d="M12 26q9 9 9 17a9 9 0 0 1-18 0c0-8 9-17 9-17Z"
      />
      {/* The calyx — the little green cup a bud sits in. Without it the shape
          reads as a balloon on a string. */}
      <path
        className="fill-leaf"
        d="M12 54q-7-1-9-6 8-3 9 6Zm0 0q7-1 9-6-8-3-9 6Z"
      />
    </svg>
  );
}

/**
 * A flower in the far row: smaller, paler, blurred, and slower.
 *
 * 🔴 ALL FOUR AT ONCE, BECAUSE DISTANCE IS NEVER ONE PROPERTY. Something is far
 * away when it is smaller AND softer AND washed out AND moving less — change
 * only the size and it reads as a small flower standing in the front row. It
 * carries no stamens and no second petal ring either: detail that would not
 * survive the blur is detail nobody sees and everybody's browser paints.
 */
function FarFlower({ className, petal }: { className: string; petal: string }) {
  return (
    <svg
      viewBox="0 0 30 100"
      className={`kq-breeze-stem absolute bottom-0 opacity-70 blur-[0.6px] ${className}`}
      style={breeze("2deg", 10.4, 5.1, "2deg")}
      role="presentation"
    >
      <path
        className="stroke-leaf"
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
        d="M14 100Q11 76 15 54"
      />
      {Array.from({ length: 5 }, (_, i) => (
        <ellipse
          key={i}
          className={petal}
          cx="15"
          cy="32"
          rx="5"
          ry="9.5"
          transform={`rotate(${String(i * 72)} 15 42)`}
        />
      ))}
      <circle className="fill-sunshine" cx="15" cy="42" r="4" />
    </svg>
  );
}
